import {observer} from "mobx-react";
import {useContext, useEffect, useState, useCallback, useRef} from "react";
import {
  Dialog,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  Chip,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon2 from "@mui/icons-material/Close";
import useWave from "use-wave";
import {Frame} from "../../common/Frame";
import {LessonEditorStoreContext} from "../../../stores/LessonEditorStore";
import {D} from "../../../Constants";
import {lessons, recording} from "../../../proto/compiled";

const formatTimestamp = (ms?: number | null) => {
  if (ms === undefined || ms === null || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const AnnotationSelectorDialog = observer(() => {
  const store = useContext(LessonEditorStoreContext);
  const [searchValue, setSearchValue] = useState("");
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search
  const debouncedSearch = useCallback((query: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      store?.loadPublishedAnnotations(query);
    }, 300);
  }, [store]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Reset search when dialog opens
  useEffect(() => {
    if (store?.annotationSelectorOpen) {
      setSearchValue("");
    }
  }, [store?.annotationSelectorOpen]);

  if (!store) return null;

  const handleClose = () => {
    store.closeAnnotationSelector();
  };

  const handleSearchChange = (query: string) => {
    setSearchValue(query);
    debouncedSearch(query);
  };

  const handleSelect = async (annotationId: string) => {
    if (store.annotationSelectorBlockId) {
      await store.linkAnnotation(store.annotationSelectorBlockId, annotationId);
    }
  };

  return (
    <Dialog
      open={store.annotationSelectorOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          style: {
            borderRadius: D.radius(2),
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }
      }}
    >
      {/* Header */}
      <Frame
        row
        alignItems="center"
        justifyContent="space-between"
        padding={3}
        style={{paddingBottom: D.unit(2), flexShrink: 0}}
      >
        <Typography variant="h5">
          Select Video Example
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon/>
        </IconButton>
      </Frame>

      {/* Search - always visible */}
      <Frame paddingH={3} style={{flexShrink: 0}}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search annotations..."
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action"/>
              </InputAdornment>
            ),
          }}
        />
      </Frame>

      {/* Scrollable results area */}
      <Frame
        direction="column"
        scroll
        padding={3}
        gap={1.5}
        style={{paddingTop: D.unit(2), flex: '1 1 auto', minHeight: 0}}
      >
        {/* Loading state */}
        {store.annotationsLoading && (
          <Frame center padding={4}>
            <CircularProgress/>
          </Frame>
        )}

        {/* Empty state */}
        {!store.annotationsLoading && (!store.publishedAnnotations || store.publishedAnnotations.length === 0) && (
          <Frame center padding={4}>
            <Typography variant="body1" color="textSecondary">
              No published annotations found.
            </Typography>
          </Frame>
        )}

        {/* Annotations list */}
        {!store.annotationsLoading && store.publishedAnnotations && store.publishedAnnotations.length > 0 && (
          store.publishedAnnotations.map((videoExample) => (
            <AnnotationItem
              key={videoExample.annotation?.id}
              videoExample={videoExample}
              onSelect={() => {
                if (videoExample.annotation?.id) {
                  handleSelect(videoExample.annotation.id);
                }
              }}
            />
          ))
        )}
      </Frame>
    </Dialog>
  );
});

interface AnnotationItemProps {
  videoExample: lessons.IVideoExample;
  onSelect: () => void;
}

const AnnotationItem = observer(({videoExample, onSelect}: AnnotationItemProps) => {
  const theme = useTheme();
  const wave = useWave();

  const annotation = videoExample.annotation;
  if (!annotation) return null;

  const isGoodPractice = annotation.annotationType === recording.AnnotationType.AT_GOOD_PRACTICE;
  const isCommonMistake = annotation.annotationType === recording.AnnotationType.AT_COMMON_MISTAKE;

  const badgeColor = isGoodPractice
    ? theme.palette.success.main
    : isCommonMistake
      ? theme.palette.error.main
      : theme.palette.grey[600];

  const badgeText = isGoodPractice
    ? "GOOD PRACTICE"
    : isCommonMistake
      ? "COMMON MISTAKE"
      : "VIDEO EXAMPLE";

  const BadgeIcon = isGoodPractice ? CheckIcon : isCommonMistake ? CloseIcon2 : null;

  return (
    <Frame
      ref={wave}
      row
      gap={2}
      padding={2}
      radius={1.5}
      clickable
      clip
      hover={theme.palette.action.hover}
      onClick={onSelect}
      style={{
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        flexShrink: 0,
      }}
    >
      {/* Video thumbnail */}
      <Frame
        radius={1}
        clip
        style={{
          width: 120,
          height: 68,
          flexShrink: 0,
          backgroundColor: '#000',
        }}
      >
        {videoExample.videoUrl && (
          <video
            src={videoExample.videoUrl}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            muted
          />
        )}
      </Frame>

      {/* Content */}
      <Frame direction="column" gap={0.5} style={{flex: '1 1 0', overflow: 'hidden', minWidth: 0}}>
        {/* Type badge */}
        <Frame row gap={0.5} alignItems="center">
          {BadgeIcon && (
            <BadgeIcon fontSize="small" style={{color: badgeColor, fontSize: 14}}/>
          )}
          <Typography
            variant="caption"
            fontWeight="bold"
            style={{color: badgeColor, letterSpacing: '0.5px', fontSize: 10}}
          >
            {badgeText}
          </Typography>
        </Frame>

        {/* Label */}
        <Typography
          variant="subtitle2"
          fontWeight={600}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {annotation.label || "Untitled"}
        </Typography>

        {/* Description */}
        {annotation.description && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              fontSize: 12,
            }}
          >
            {annotation.description}
          </Typography>
        )}

        {/* Time and recording */}
        <Frame row gap={1} alignItems="center" marginTop={0.5}>
          <Chip
            label={`${formatTimestamp(annotation.startMs)}${annotation.endMs ? ` → ${formatTimestamp(annotation.endMs)}` : ''}`}
            size="small"
            variant="outlined"
            sx={{height: 20, fontSize: 11}}
          />
          {videoExample.recordingName && (
            <Typography variant="caption" color="textSecondary" style={{fontSize: 11}}>
              {videoExample.recordingName}
            </Typography>
          )}
        </Frame>
      </Frame>
    </Frame>
  );
});
